import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useMemo } from "react";
import "./view.scss";
/**
 * Email-block card content.
 *
 * Two render modes, dispatched via `BlockParams.isExpanded`:
 *
 *   - **Card** (default): plaintext-only preview, line-clamped to fit the
 *     card chrome. Smaller type so the body gets the room. The "From:"
 *     label only renders when the email was forwarded — for direct mail
 *     the sender is the account owner and the label is redundant noise.
 *
 *   - **Detail** (`isExpanded`): full HTML body in a sandboxed iframe,
 *     rendered exactly as the email arrived (post-sanitization).
 *     `sandbox=""` (no flags) means scripts can't execute, forms can't
 *     submit, navigation is blocked, and the iframe runs as an opaque
 *     origin with no cookies. Stored sanitized HTML is defense in
 *     depth; the iframe is the trust boundary.
 */
export const EmailBlockView = memo(({ block, blockParams }) => {
    const content = block.content;
    const isExpanded = blockParams?.isExpanded === true;
    const sentLabel = useMemo(() => {
        if (!content.sentAt)
            return undefined;
        const d = new Date(content.sentAt);
        if (Number.isNaN(d.getTime()))
            return undefined;
        return d.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    }, [content.sentAt]);
    // For direct mail, `from` is always the account owner (we matched the
    // inbound on it), which makes the label redundant. We only show it
    // when the email was forwarded — and we show the *original* sender,
    // not the forwarder.
    const senderLabel = content.forwardedFrom
        ? formatAddress(content.forwardedFrom)
        : undefined;
    return (_jsxs("div", { className: "card-content email-block", children: [content.subject ? (_jsx("h3", { className: "email-block__subject", children: content.subject })) : null, senderLabel || sentLabel ? (_jsxs("div", { className: "email-block__meta", children: [senderLabel ? _jsxs("span", { children: ["From: ", senderLabel] }) : null, senderLabel && sentLabel ? _jsx("span", { children: " \u00B7 " }) : null, sentLabel ? _jsx("span", { children: sentLabel }) : null] })) : null, isExpanded ? _jsx(ExpandedBody, { content: content }) : _jsx(PreviewBody, { content: content }), content.attachments && content.attachments.length > 0 ? (_jsx("ul", { className: "email-block__attachments", children: content.attachments.map((att, i) => (_jsx("li", { children: att.type === "external" && att.url ? (_jsx("a", { href: att.url, target: "_blank", rel: "noopener noreferrer", children: filenameFor(att, i) })) : (_jsx("span", { children: filenameFor(att, i) })) }, i))) })) : null] }));
});
/**
 * Card-mode body: plaintext only, line-clamped. We never render HTML
 * here — too risky for the small, ubiquitous card surface, and the
 * plaintext (already derived at ingest from the HTML when not provided
 * by the sender) carries the readable content.
 *
 * If the plaintext was offloaded to R2, we just don't render anything
 * here — the card surface isn't worth a fetch round-trip and we'd
 * rather a clean "subject + meta" card than spinners. The detail view
 * handles oversized bodies properly via the iframe `src` route.
 */
const PreviewBody = ({ content }) => {
    const text = content.bodyText;
    if (!text)
        return null;
    return (_jsx("div", { className: "email-block__body email-block__body--preview", children: text }));
};
/**
 * Detail-mode body: full HTML in a sandboxed iframe so the email
 * renders as it arrived. Falls through to plaintext / R2-text-file
 * when the message has no HTML part.
 *
 * Sandbox flags:
 *   - `allow-popups` — without this, link clicks inside the iframe are
 *     silently dropped (the `<base target="_blank">` injected by
 *     `wrapEmailHtml` has nowhere to land). With it, clicking a link
 *     attempts a new tab.
 *   - `allow-popups-to-escape-sandbox` — by default popups inherit the
 *     opener's sandbox, so the new tab would also be script-free and
 *     unable to render most modern pages. This flag drops the sandbox
 *     on the popup itself, so the destination loads as a normal page
 *     in the user's browser.
 *
 * We intentionally do NOT add `allow-scripts`, `allow-forms`,
 * `allow-same-origin`, or `allow-top-navigation` — those would let the
 * email content escape the iframe or fingerprint the user. With just
 * the two popup flags, the iframe stays an opaque-origin, script-free
 * sandbox, but links to external sites become clickable.
 */
const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";
const ExpandedBody = ({ content }) => {
    const subject = content.subject || "Email body";
    if (content.bodyHtml) {
        return (_jsx("iframe", { className: "email-block__body email-block__body--frame", title: subject, sandbox: IFRAME_SANDBOX, srcDoc: content.bodyHtml }));
    }
    const htmlFileUrl = externalUrl(content.bodyHtmlFile);
    if (htmlFileUrl) {
        return (_jsx("iframe", { className: "email-block__body email-block__body--frame", title: subject, sandbox: IFRAME_SANDBOX, src: htmlFileUrl }));
    }
    if (content.bodyText) {
        return (_jsx("pre", { className: "email-block__body email-block__body--text", children: content.bodyText }));
    }
    const textFileUrl = externalUrl(content.bodyTextFile);
    if (textFileUrl) {
        return (_jsx("iframe", { className: "email-block__body email-block__body--frame", title: subject, sandbox: IFRAME_SANDBOX, src: textFileUrl }));
    }
    return null;
};
function externalUrl(file) {
    if (!file)
        return undefined;
    if (file.type !== "external")
        return undefined;
    return file.url;
}
function formatAddress(a) {
    return a.name ? `${a.name} <${a.address}>` : a.address;
}
function filenameFor(att, i) {
    if (att.filename)
        return att.filename;
    if (att.url) {
        try {
            const u = new URL(att.url);
            return u.pathname.split("/").pop() ?? `attachment-${i + 1}`;
        }
        catch {
            return `attachment-${i + 1}`;
        }
    }
    return `attachment-${i + 1}`;
}
